// Live updates: a screen reloads itself when what it shows has changed.
//
// Every screen loaded once and then only when the person looking at it acted.
// Finance signing on their own machine left the approver's page reading OPEN
// until somebody pressed F5; a panel on the same page could say DONE while
// the banner above it still said OPEN.
//
// The server announces each commit on /api/live (server-sent events): which
// tables changed and, for items, which codes (app/services/live.py). This file
// keeps one connection per tab and lets a component say "reload me when one
// of these changes" with `useLive`.
//
// fetch() rather than EventSource, because EventSource cannot send the
// Authorization header and the token does not belong in a URL, where it would
// be written into every proxy log.
//
// Two nets under it, because a live connection can drop without anyone
// noticing: a tab coming back into view reloads everything it shows, and while
// the stream is down every subscriber reloads once a minute.

import { useEffect, useRef } from "react";

const listeners = new Set();
let started = false;
let connected = false;
let controller = null;
let pollTimer = null;
let hiddenAt = null;

const ALL = { all: true };

function emit(msg) {
  for (const fn of Array.from(listeners)) {
    try { fn(msg); } catch (e) { /* one screen's error is not another's */ }
  }
}

function setConnected(v) {
  connected = v;
  if (v && pollTimer) { clearInterval(pollTimer); pollTimer = null; }
  if (!v && !pollTimer && started) pollTimer = setInterval(() => emit(ALL), 60000);
}

async function run(url, getToken) {
  let backoff = 3000;
  while (started) {
    const token = getToken();
    if (!token) { await sleep(5000); continue; }
    controller = new AbortController();
    try {
      const r = await window.fetch(url, {
        headers: { Authorization: `Bearer ${token}`, Accept: "text/event-stream" },
        signal: controller.signal, cache: "no-store",
      });
      if (!r.ok || !r.body) throw new Error(`live ${r.status}`);
      const wasDown = !connected;
      setConnected(true);
      backoff = 3000;
      // Whatever changed while we were not listening, we did not hear about.
      if (wasDown) emit(ALL);
      const reader = r.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        let i;
        while ((i = buf.indexOf("\n\n")) >= 0) {
          const block = buf.slice(0, i);
          buf = buf.slice(i + 2);
          let ev = "message", data = "";
          for (const line of block.split("\n")) {
            if (line.startsWith("event:")) ev = line.slice(6).trim();
            else if (line.startsWith("data:")) data += line.slice(5).trim();
          }
          if (ev === "message" && data) {
            try { emit(JSON.parse(data)); } catch { /* malformed: ignore */ }
          }
        }
      }
    } catch (e) {
      if (!started) break;
    }
    setConnected(false);
    await sleep(backoff);
    backoff = Math.min(backoff * 2, 30000);
  }
}

const sleep = (ms) => new Promise(r => setTimeout(r, ms));

function onVisibility() {
  if (document.hidden) { hiddenAt = Date.now(); return; }
  if (hiddenAt && Date.now() - hiddenAt > 5000) emit(ALL);
  hiddenAt = null;
}

/** Open the live connection. Call once after sign-in; safe to call again. */
export function startLive(url, getToken) {
  if (started) return;
  started = true;
  document.addEventListener("visibilitychange", onVisibility);
  run(url, getToken);
}

/** Close it — on sign-out. */
export function stopLive() {
  started = false;
  document.removeEventListener("visibilitychange", onVisibility);
  if (controller) controller.abort();
  if (pollTimer) { clearInterval(pollTimer); pollTimer = null; }
  connected = false;
}

// ── Matchers ────────────────────────────────────────────────────────────────

/** Everything that decides an item's status, sign-offs and queues. */
export const ITEM_TABLES = ["items", "item_assignments", "item_composite_reviews",
                            "item_components", "item_documents"];

/** Does this message touch any of `tables`? */
export const touches = (tables) => (msg) =>
  !!msg.all || tables.some(t => msg.tables && msg.tables[t]);

/** Does it touch this one item? A table that changed without naming its item
 *  codes counts — reloading once too often is cheap, missing a change is not. */
export const touchesItem = (code, tables = ITEM_TABLES) => (msg) => {
  if (msg.all) return true;
  if (!code || !msg.tables) return false;
  return tables.some(t => {
    const c = msg.tables[t];
    if (!c) return false;
    if (c.many || !c.codes || c.codes.length === 0) return true;
    return c.codes.includes(code);
  });
};

/** Reload when a matching change arrives.
 *
 *  `match` is a list of table names or a function of the message. Bursts are
 *  merged: a bulk stamp is one reload, not fifty. `reload` may change between
 *  renders; the latest one is called. */
export function useLive(match, reload, { delay = 400 } = {}) {
  const r = useRef(reload);
  const m = useRef(match);
  r.current = reload;
  m.current = match;
  useEffect(() => {
    let t = null;
    const fn = (msg) => {
      const mm = m.current;
      const hit = typeof mm === "function" ? mm(msg) : touches(mm)(msg);
      if (!hit) return;
      clearTimeout(t);
      t = setTimeout(() => { r.current && r.current(); }, delay);
    };
    listeners.add(fn);
    return () => { listeners.delete(fn); clearTimeout(t); };
  }, [delay]);
}
